import { MessageContext } from 'contexts/MessageContext';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { toast } from 'sonner';

import {
    ChainlitContext,
    type IAction,
    sessionIdState
} from '@chainlit/react-client';

import Icon from '@/components/Icon';
import { Loader } from '@/components/Loader';
import { Translator } from '@/components/i18n';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger
} from '@/components/ui/tooltip';

interface ActionProps {
    action: IAction;
}

const ActionButton = ({ action }: ActionProps) => {
    const { loading, askUser } = useContext(MessageContext);
    const apiClient = useContext(ChainlitContext);
    const sessionId = useRecoilValue(sessionIdState);
    const [isRunning, setIsRunning] = useState(false);

    const content = useMemo(() => {
        return action.icon
            ? action.label
            : action.label
            ? action.label
            : action.name;
    }, [action]);

    const icon = useMemo(() => {
        if (isRunning) return <Loader />;
        if (action.icon) return <Icon name={action.icon as any} />;
        return null;
    }, [action, isRunning]);

    const handleClick = useCallback(async () => {
        try {
            setIsRunning(true);
            await apiClient.callAction(action, sessionId);
        } catch (err) {
            toast.error(String(err));
        } finally {
            setIsRunning(false);
        }
    }, [action, sessionId, apiClient]);

    const isAskingAction = askUser?.spec.type === 'action';
    const ignore = isAskingAction && askUser?.spec.keys?.includes(action.id);

    if (ignore) return null;

    const button = (
        <Button
            id={action.id}
            onClick={handleClick}
            size="icon-sm"
            variant="ghost"
            className="text-muted-foreground"
            disabled={loading || isRunning}
        >
            {icon}
            {content}
        </Button>
    );

    // Special handling for delete message button with AlertDialog
    if (action.name === 'delete_message_button') {
        return (
            <AlertDialog>
                <AlertDialogTrigger asChild>
                    <Button
                        id={action.id}
                        size="icon-sm"
                        variant="ghost"
                        className="text-muted-foreground"
                        disabled={loading || isRunning}
                    >
                        {icon}
                        {content}
                    </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            <Translator path="chat.messages.actions.delete.title" />
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            <Translator path="chat.messages.actions.delete.description" />
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>
                            <Translator path="common.actions.cancel" />
                        </AlertDialogCancel>
                        <AlertDialogAction variant="destructive" onClick={handleClick}>
                                <Translator path="common.actions.confirm" />
                            </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        );
    }

    if (action.tooltip) {
        return (
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>{button}</TooltipTrigger>
                    <TooltipContent>
                        <p>{action.tooltip}</p>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        );
    } else {
        return button;
    }
};

export { ActionButton };
